import { initCropper, initProductImageCropper } from '../CoverImageControl/cropper';
import { Dialog } from '../Dialog/Dialog';
import { GalleryDialogBannerSettings } from './GalleryDialogBannerSettings';
import { GalleryDialogFooter } from './GalleryDialogFooter';
import { GalleryDialogPreview } from './GalleryDialogPreview';
import { GalleryDialogZoom } from './GalleryDialogZoom';
import { GalleryItem, HorizontalAlignment, VerticalAlignment } from './types';
import { Box, makeStyles } from '@material-ui/core';
import Cropper from 'cropperjs';
import React, { useCallback, useEffect, useState } from 'react';
import { CropperMode } from 'components/CoverImageControl/types';

const useStyles = makeStyles({
    content: {
        padding: '0 !important',
    },
    footer: {
        borderTop: '1px solid #eee',
    },
});

export interface GalleryDialogProps {
    image: GalleryItem;
    previewRef: HTMLDivElement;
    mode?: CropperMode;
    hideBannerSettings?: boolean;
    aspectRatio?: number;  
    onSave: (data: GalleryItem) => void;
}

export const GalleryDialog = (props: GalleryDialogProps) => {
    const { image, previewRef, onSave, hideBannerSettings, aspectRatio = 2, mode } = props;

    const classes = useStyles();

    const width = 600;

    const [imgRef, setImgRef] = useState(null);
    const [cropBoxData, setCropBoxData] = useState<Cropper.CropBoxData | null>(null);
    const [zoom, setZoom] = useState(1);

    const [croppedArea, setCroppedArea] = useState<any>();
    const [cropper, setCropper] = useState<Cropper | null>(null);
    const [label, setLabel] = useState(image.label ?? '');
    const [color, setColor] = useState(image.color ?? '#ffffff');
    const [horizontalAlignment, setHorizontalAlignment] = useState(
        image.horizontalAlignment ?? HorizontalAlignment.LEFT,
    );
    const [verticalAlignment, setVerticalAlignment] = useState(image.verticalAlignment ?? VerticalAlignment.TOP);

    useEffect(() => {
        if (!imgRef) {
            return;
        }

        const cropperInstance = new Cropper(imgRef, {
            aspectRatio,
            preview: [previewRef],
            cropBoxMovable: false,
            cropBoxResizable: false,
            viewMode: mode === CropperMode.PRODUCT_IMAGE ? 0 : 1,
            ready() {
                const { cropper } = this as { cropper: Cropper };
                const cropBoxData = cropper.getCropBoxData();
                setCropBoxData(cropBoxData);
                if (mode === CropperMode.PRODUCT_IMAGE) {
                    const initialZoom = initProductImageCropper(cropper);
                    setZoom(initialZoom);
                } else {
                    const initialZoom = initCropper(cropper);
                    setZoom(initialZoom);
                }
            },
            crop() {
                const { cropper } = this as { cropper: Cropper };
                const cropBoxData = cropper.getCropBoxData();
                setCropBoxData(cropBoxData);
                setCroppedArea(cropper.getData());
            },
            zoom(event: Cropper.ZoomEvent) {
                const { cropper } = this as { cropper: Cropper };
                setZoom(event.detail.ratio);
                setCroppedArea(cropper.getData());
            },
        });
        setCropper(cropperInstance);
    }, [imgRef, aspectRatio, previewRef, setCropBoxData, setCroppedArea, setZoom, setCropper]);

    const handleSave = useCallback(() => {
        onSave({
            croppedArea,
            label,
            color,
            horizontalAlignment,
            verticalAlignment,
        });
    }, [croppedArea, label, color, horizontalAlignment, verticalAlignment, onSave]);

    const src = props.image.dataURL ?? `/api/images/${props.image.imageId}`;

    const handleZoomChange = useCallback(
        (newZoomValue: number) => {
            setZoom(newZoomValue);
            if (cropper) {
                cropper.zoomTo(newZoomValue);
            }
        },
        [cropper, setZoom],
    );

    return (
        <Dialog contentStyles={classes.content} width={width}>
            <Box flex="1">
                <GalleryDialogPreview
                    aspectRatio={aspectRatio}
                    src={src}
                    color={color}
                    horizontalAlignment={horizontalAlignment}
                    verticalAlignment={verticalAlignment}
                    text={label}
                    imgRef={setImgRef}
                    cropBoxData={cropBoxData}
                    zoom={zoom}
                />
                <GalleryDialogZoom zoom={zoom} onZoomChange={handleZoomChange} />
                {!hideBannerSettings && <GalleryDialogBannerSettings
                    bannerText={label}
                    onBannerTextChange={setLabel}
                    horizontalAlignment={horizontalAlignment}
                    onHorizontalAlignmentChange={setHorizontalAlignment}
                    verticalAlignment={verticalAlignment}
                    onVerticalAlignmentChange={setVerticalAlignment}
                    color={color}
                    onColorChange={setColor}
                />
                }
                <footer className={classes.footer}>
                    <GalleryDialogFooter onSave={handleSave} />
                </footer>
            </Box>
        </Dialog>
    );
};
